import { useCallback } from "react";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import type { AssistantValue } from "../../../services/api/instances/assistantsApi/types";
import type { DialogValue } from "../../../services/api/instances/dialogApi/types";
import type { TModelItem } from "../../../services/api/instances/modelsApi/types";
import type { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectDialogValue } from "../redux/selectors";
import { setDialogSelectedAssistant, updateDialogField } from "../redux/slice";

export const useDialogSelections = () => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectDialogValue);
  const { selectedObject } = useStorageSelectors("dialogs");

  const handleSelectAssistant = useCallback(
    (assistant: NormalizedStorageObject<AssistantValue> | null) => {
      dispatch(setDialogSelectedAssistant(assistant));
      const modelName = value?.dialog_object?.model || null;
      const ref = assistant ? assistant.ref : null;
      dispatch(
        updateDialogField({
          assistant_ref: ref,
          model: modelName,
        }),
      );
      if (value?.dialog_object) {
        const updatedValue: DialogValue = {
          dialog_object: {
            ...value.dialog_object,
            assistant_ref: ref,
            model: modelName,
          },
        };
        dispatch(
          editStorageObject({
            id: selectedObject?.id as string,
            name: selectedObject?.friendly_name as string,
            value: updatedValue,
            service: "dialogs",
          }),
        );
      }
    },
    [dispatch, value, selectedObject],
  );

  const handleSelectModel = useCallback(
    (model: TModelItem | null) => {
      if (!model) return;
      const modelName = model ? model.id : null;
      dispatch(
        updateDialogField({
          assistant_ref: modelName && null,
          model: modelName,
        }),
      );
      if (value?.dialog_object) {
        const updatedValue: DialogValue = {
          dialog_object: {
            ...value.dialog_object,
            assistant_ref: modelName && null,
            model: modelName,
          },
        };
        dispatch(
          editStorageObject({
            id: selectedObject?.id as string,
            name: selectedObject?.friendly_name as string,
            value: updatedValue,
            service: "dialogs",
          }),
        );
      }
    },
    [dispatch, value, selectedObject],
  );

  return {
    value,
    handleSelectAssistant,
    handleSelectModel,
  };
};
